import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { File, Folder, Download, ArrowLeft, Lock, Unlock, Share2, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/Toast';
import Button from '../components/Button';

interface SharedItem {
    id: string;
    nome: string;
    tipo: 'arquivo' | 'pasta';
    tamanho: number;
    compartilhado: boolean;
    exige_senha: boolean;
    url_arquivo?: string;
    caminho?: string;
}

export default function SharedDrive() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { showSuccess, showError, toasts } = useToast();

    const [item, setItem] = useState<SharedItem | null>(null);
    const [items, setItems] = useState<SharedItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (token) {
            loadSharedItem();
        }
    }, [token]);

    const loadSharedItem = async () => {
        if (!token) return;

        try {
            setLoading(true);
            setError('');

            // Buscar item compartilhado pelo token usando RPC
            const { data, error: queryError } = await supabase.rpc('buscar_item_compartilhado', {
                token_input: token
            });

            if (queryError) throw queryError;

            const dataItem = Array.isArray(data) ? data[0] : data;

            if (!dataItem) {
                setError('Item compartilhado não encontrado');
                return;
            }

            // Gerar URL para o arquivo
            let itemWithUrl = { ...dataItem };
            if (dataItem.tipo === 'arquivo' && dataItem.caminho) {
                const { data: urlData } = supabase.storage
                    .from('user-driver')
                    .getPublicUrl(dataItem.caminho);
                itemWithUrl = { ...itemWithUrl, url_arquivo: urlData.publicUrl };
            }

            setItem(itemWithUrl as SharedItem);

            // Se não exige senha, já considerar autenticado
            if (!dataItem.exige_senha) {
                setAuthenticated(true);
            }

            // Se exige senha e não está autenticado, mostrar modal
            if (dataItem.exige_senha && !authenticated) {
                setShowPasswordModal(true);
                setLoading(false);
                return;
            }

            // Se é pasta (com ou sem senha), buscar filhos
            // authenticated é true se passou pela senha ou se não exige senha
            if (dataItem.tipo === 'pasta') {
                await loadSharedFolderItems(dataItem.id);
            }

            setLoading(false);
        } catch (err: any) {
            setError('Erro ao carregar item compartilhado');
            showError('Erro', 'Não foi possível carregar o item compartilhado');
            setLoading(false);
        }
    };

    const loadSharedFolderItems = async (pastaId: string) => {
        try {
            const { data, error } = await supabase.rpc('buscar_filhos_compartilhados', {
                pasta_id: pastaId,
                token_input: token || ''
            });

            if (error) throw error;

            // Gerar URLs para os arquivos
            const itemsWithUrls = (data || []).map((file: any) => {
                if (file.tipo === 'arquivo' && file.caminho) {
                    const { data: urlData } = supabase.storage
                        .from('user-driver')
                        .getPublicUrl(file.caminho);
                    return { ...file, url_arquivo: urlData.publicUrl };
                }
                return file;
            });

            setItems(itemsWithUrls);
        } catch (err) {
            // Erro silencioso ao carregar itens da pasta
        }
    };

    const handlePasswordSubmit = async () => {
        if (!password.trim() || !token || !item || isSubmitting) return;

        setIsSubmitting(true);
        try {
            // Verificar senha usando RPC
            const { data, error: queryError } = await supabase.rpc('verificar_senha_compartilhamento', {
                token_input: token,
                senha_input: password
            });

            if (queryError) throw queryError;

            if (data === true) {
                setAuthenticated(true);
                setShowPasswordModal(false);
                setError('');

                // Carregar conteúdo sem verificar senha novamente
                if (item.tipo === 'pasta') {
                    await loadSharedFolderItems(item.id);
                }
            } else {
                setError('Senha incorreta');
                showError('Erro', 'Senha incorreta. Tente novamente');
            }
        } catch (err: any) {
            setError('Erro ao verificar senha');
            showError('Erro', 'Não foi possível verificar a senha');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownload = async (item: SharedItem) => {
        if (!item.url_arquivo) {
            showError('Erro', 'URL do arquivo não encontrada');
            return;
        }

        try {
            // Tentar baixar usando fetch
            const response = await fetch(item.url_arquivo);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.nome;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            showSuccess('Download iniciado', `${item.nome} está sendo baixado`);
        } catch (err) {
            showError('Erro', `Não foi possível baixar o arquivo: ${err}`);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 pt-8 pb-4" style={{ minHeight: '70px', paddingBottom: '10px' }}>
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={() => navigate('/cursos')}
                            variant="btn_add"
                            className="flex items-center gap-2"
                        >
                            <ArrowLeft size={20} />
                            Voltar
                        </Button>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Arquivo Compartilhado</h1>
                    </div>
                    {item && (
                        <div className="flex items-center gap-2">
                            {item.compartilhado && (
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        {item.exige_senha ? (
                                            <Lock size={16} className="text-green-600 dark:text-green-400" />
                                        ) : (
                                            <Unlock size={16} className="text-yellow-600 dark:text-yellow-400" />
                                        )}
                                        <Share2 size={16} className="text-blue-600 dark:text-blue-400" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="text-red-800 dark:text-red-400">{error}</span>
                    </div>
                )}

                {/* Item principal */}
                {item && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                        <div className="flex items-center gap-4">
                            {item.tipo === 'pasta' ? (
                                <Folder size={48} className="text-blue-500 dark:text-blue-400" />
                            ) : (
                                <File size={48} className="text-gray-500 dark:text-gray-400" />
                            )}
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{item.nome}</h2>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {item.tipo === 'pasta' ? 'Pasta compartilhada' : `Tamanho: ${formatFileSize(item.tamanho)}`}
                                </p>
                            </div>
                            {item.tipo === 'arquivo' && (
                                <Button
                                    onClick={() => handleDownload(item)}
                                    variant="btn_add"
                                    className="flex items-center gap-2"
                                >
                                    <Download size={20} />
                                    Baixar Arquivo
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Itens da pasta */}
                {item?.tipo === 'pasta' && items.length > 0 && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Arquivos na pasta
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {items.map((file) => (
                                <div
                                    key={file.id}
                                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <File size={32} className="text-gray-500 dark:text-gray-400" />
                                        <div className="flex-1">
                                            <h4
                                                className="font-medium text-xs text-gray-900 dark:text-gray-100 leading-snug overflow-hidden"
                                                style={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    wordBreak: 'break-word'
                                                }}
                                                title={file.nome}
                                            >
                                                {file.nome}
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {formatFileSize(file.tamanho)}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => handleDownload(file)}
                                        variant="btn_add"
                                        className="w-full flex items-center justify-center gap-2"
                                    >
                                        <Download size={16} />
                                        Baixar
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Password Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center gap-3 mb-4">
                            <Lock size={32} className="text-blue-600 dark:text-blue-400" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    Pasta Protegida
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Esta pasta requer senha para acesso
                                </p>
                            </div>
                        </div>
                        <div className="mb-4">
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isSubmitting) {
                                        e.preventDefault();
                                        handlePasswordSubmit();
                                    }
                                }}
                                placeholder="Digite a senha"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                                autoFocus
                            />
                        </div>
                        {error && (
                            <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
                        )}
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => navigate('/cursos')}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={(e?) => {
                                    if (e) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                    handlePasswordSubmit();
                                }}
                                variant="btn_add"
                                disabled={!password.trim() || isSubmitting}
                            >
                                {isSubmitting ? 'Verificando...' : 'Acessar'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    );
}

